/**
 * Time-log (activities) WRITE layer — Oxbo-P.
 *
 * Injected into the shared `pApi` via `injectEndpoints` so the contract files
 * (`src/api/pApi.ts`, `src/api/types.ts`) stay untouched and branches merge
 * cleanly. The read side (`useGetEventActivitiesQuery`) already lives on pApi;
 * these mutations POST/PUT a day's activity rows and invalidate the event's
 * `Activity` list tag so the panel refetches on success.
 *
 * Contract (all under /v1, JWT auth; PUT keys on `entity_id`, NOT `id`):
 *   POST /reports/{report_id}/activities
 *   PUT  /reports/{report_id}/activities/{entity_id}
 */
import { pApi } from "@api/pApi";
import type { ActivityOut } from "@api/types";

/**
 * The server response carries a stable `entity_id` (the versioned identity PUT
 * keys on) that the shared `ActivityOut` type predates. We extend it locally so
 * the panel can read `entity_id` without touching the contract type file.
 */
export interface ActivityRow extends ActivityOut {
  entity_id: string;
  version?: number;
}

/** Body accepted by POST/PUT `/reports/{report_id}/activities`. */
export interface ActivityWrite {
  sequence_no?: number | null;
  time_from?: string | null;
  time_to?: string | null;
  duration_hours?: number | null;
  md_from?: number | null;
  md_to?: number | null;
  phase_entry_key?: string | null;
  activity_code_entry_key?: string | null;
  activity_subcode_entry_key?: string | null;
  npt_level?: number | null;
  npt_class_entry_key?: string | null;
  npt_reason_key?: string | null;
  time_class?: "productive" | "npt" | "flat_waiting" | null;
  tour?: string | null;
  service_company_entry_key?: string | null;
  activity_memo?: string | null;
  change_reason?: string | null;
}

export const activityApi = pApi.injectEndpoints({
  endpoints: (b) => ({
    // Create a new time-log row on the given daily report. Refetch the event's
    // activities list on success (the list query is keyed by event_id).
    createActivity: b.mutation<
      ActivityRow,
      { reportId: string; eventId: string; body: ActivityWrite }
    >({
      query: ({ reportId, body }) => ({
        url: `/reports/${reportId}/activities`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, { eventId }) => [
        { type: "Activity" as const, id: `EVENT-${eventId}` },
      ],
    }),
    // Edit an existing row. Keyed on entity_id (the versioned identity), not id.
    updateActivity: b.mutation<
      ActivityRow,
      { reportId: string; entityId: string; eventId: string; body: ActivityWrite }
    >({
      query: ({ reportId, entityId, body }) => ({
        url: `/reports/${reportId}/activities/${entityId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_r, _e, { eventId }) => [
        { type: "Activity" as const, id: `EVENT-${eventId}` },
      ],
    }),
  }),
  overrideExisting: false,
});

export const { useCreateActivityMutation, useUpdateActivityMutation } = activityApi;
